import { message } from 'antd';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'underscore';
import { IBestDataset, IPlotVariable } from '../../../../types/analytics';
import { IGetBasisChartV2ApiPayload } from '../../../../types/chart';
import { MarkerItem } from '../../../../types/home';
import { IAnalytcisUserInput } from '../../../../types/IAnalyticsUserInput';
import { IEnrichedMetadata } from '../../../../types/metadata';
import { SplitCoordinates } from '../../../../utils/coordinate';
import { DT0Format } from '../../../../utils/StaticVariable';

interface UseTSPlotParams {
  datasets: IBestDataset[];
  variables: IPlotVariable[];
  defaultValueShortName?: string | null;
  globalDateRange: any[];
  setGlobalDateRange: (item: any) => void;
  previewDatasetId: string;
  previewDatasetTimeRange: [moment.Moment, moment.Moment];
  tsData: { [key: string]: any }[];
  onUpdate: (values: IGetBasisChartV2ApiPayload) => void;
  tsError: string;
  updatedTSData: any[];
  pointProps: string;
  pointName: string;
  userInput: IAnalytcisUserInput;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  loadingButtonUpdate: boolean;
  errorUpdate: string;
  point?: MarkerItem;
  isForAllAnalytics: boolean;
  setIsForAllAnalytics: (item: any) => void;
  onDatasetChange: (item: string) => void;
  coordinates: number[];
  enrichedMetadata: IEnrichedMetadata[];
}

export const useTimeSeriesParams = (params: UseTSPlotParams) => {
  const {
    datasets,
    variables,
    defaultValueShortName,
    globalDateRange,
    setGlobalDateRange,
    previewDatasetTimeRange,
    tsData,
    onUpdate,
    updatedTSData,
    userInput,
    updateUserInput,
    isForAllAnalytics,
    setIsForAllAnalytics,
    pointProps,
    previewDatasetId,
    coordinates,
    enrichedMetadata,
  } = params;

  const selfTriggered = useRef(false);

  const [tsPlotData, setTSPlotData] = useState<any[]>([]);
  const [variable, setVariable] = useState<IPlotVariable | null>(
    userInput.TimeSeriesPlot.variable
  );
  const [tempVariable, setTempVariable] = useState<IPlotVariable | null>(
    userInput.TimeSeriesPlot.variable
  );
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState(
    userInput?.TimeSeriesPlot?.startDate ??
      moment(previewDatasetTimeRange[1]).subtract(1, 'years')
  );
  const [endDate, setEndDate] = useState(
    userInput?.TimeSeriesPlot?.endDate ?? moment(previewDatasetTimeRange[1])
  );
  const [previewSettingChanged, setPreviewSettingChanged] = useState(false);

  const onUpdateTSPlot = useCallback(
    (dateRange?: any) => {
      if (tempVariable) {
        const orderId = enrichedMetadata.find(
          (item) =>
            item.CoordinatesString === coordinates.join(',') &&
            item.DatasetId === previewDatasetId
        )?.OrderId;
        const values: IGetBasisChartV2ApiPayload = {
          orderId: orderId,
          datasetName: previewDatasetId,
          parameters: tempVariable.Name,
          startDate: dateRange
            ? dateRange[0].format(DT0Format)
            : startDate.format(DT0Format),
          endDate: dateRange
            ? dateRange[1].format(DT0Format)
            : endDate.format(DT0Format),
        };
        setVariable(tempVariable);
        onUpdate(values);
      }
    },
    [startDate, endDate, tempVariable, onUpdate, enrichedMetadata, coordinates]
  );

  const disabledStartDate = useCallback(
    (current: Moment) => {
      const isAfterEndDate = current && current > endDate;
      const isBeforeDatasetStart =
        current && current < previewDatasetTimeRange[0].startOf('day');

      return isAfterEndDate || isBeforeDatasetStart;
    },
    [endDate, previewDatasetTimeRange]
  );

  const disabledEndDate = useCallback(
    (current: Moment) => {
      const isBeforeStartDate = current && current < startDate;
      const isAfterDatasetEnd =
        current && current > previewDatasetTimeRange[1].endOf('day');

      return isBeforeStartDate || isAfterDatasetEnd;
    },
    [startDate, previewDatasetTimeRange]
  );

  const handleCheckboxChange = useCallback(() => {
    selfTriggered.current = true;
    setGlobalDateRange([startDate, endDate]);
    setIsForAllAnalytics(!isForAllAnalytics);
  }, [
    startDate,
    endDate,
    setGlobalDateRange,
    isForAllAnalytics,
    setIsForAllAnalytics,
  ]);

  const doVariableChange = useCallback(
    (active: number) => {
      const item = variables.find((a) => a.Index === active)!;
      setPreviewSettingChanged(true);
      setTempVariable(item);
    },
    [variables]
  );

  const doStartDateChange = useCallback(
    (value: any) => {
      if (value) {
        setPreviewSettingChanged(true);
        setStartDate(value);
        if (isForAllAnalytics) {
          setGlobalDateRange([value, endDate]);
        }
      }
    },
    [endDate, isForAllAnalytics, setGlobalDateRange]
  );

  const doEndDateChange = useCallback(
    (value: any) => {
      if (value) {
        setPreviewSettingChanged(true);
        setEndDate(value);
        if (isForAllAnalytics) {
          setGlobalDateRange([startDate, value]);
        }
      }
    },
    [startDate, isForAllAnalytics, setGlobalDateRange]
  );

  const categories = useMemo(() => {
    const label = datasets.map((bd: IBestDataset) => bd.label);
    return Array.from(new Set(label));
  }, [datasets]);

  const selectedDatasetInfo = useMemo(() => {
    return datasets.find((d) => d.id === previewDatasetId);
  }, [datasets]);

  const memoCoords = useMemo(() => {
    return SplitCoordinates(pointProps);
  }, [pointProps]);

  useEffect(() => {
    updateUserInput({
      TimeSeriesPlot: {
        dataset: previewDatasetId,
        variable: variable,
        startDate: startDate,
        endDate: endDate,
      },
    });
  }, [previewDatasetId, variable, startDate, endDate, updateUserInput]);

  useEffect(() => {
    if (variable !== undefined) {
      const dataKey = variable?.Name.split(' ').join('_') || '';
      const firstTSData =
        updatedTSData && updatedTSData[0] ? updatedTSData[0] : null;

      if (variable && firstTSData && dataKey in firstTSData) {
        setName(`${variable.Name} [[${variable.Unit}]]`);
        setTSPlotData(
          updatedTSData.map((ts) => ({
            Time: moment(ts.Time, 'YYYY-MM-DD HH:mm:ss').toDate(),
            Value: ts[dataKey],
          }))
        );
      } else if (tsData.length === 0) {
        setTSPlotData([]);
        setName('');
      }
    }
  }, [updatedTSData, variable, tsData]);

  useEffect(() => {
    previewSettingChanged &&
      message.warning('Please click Update once you finished your selection');
  }, [previewSettingChanged]);

  useEffect(() => {
    if (updatedTSData.length > 0) return;
    if (tsData.length > 0 && !isEmpty(variables) && defaultValueShortName) {
      if (
        userInput.TimeSeriesPlot.startDate &&
        userInput.TimeSeriesPlot.endDate
      ) {
        setStartDate(userInput.TimeSeriesPlot.startDate);
        setEndDate(userInput.TimeSeriesPlot.endDate);
      } else {
        setStartDate(moment(previewDatasetTimeRange[1]).subtract(1, 'years'));
        setEndDate(moment(previewDatasetTimeRange[1]));
      }
      const restoredVariable = userInput.TimeSeriesPlot.variable
        ? variables.find(
            (v) => v.Index === userInput.TimeSeriesPlot.variable?.Index
          )
        : undefined;
      const defaultVariable = variables.find(
        (v: IPlotVariable) => v.ShortName === defaultValueShortName
      );
      const selectedVariable =
        restoredVariable ?? defaultVariable ?? variables[0];

      setTempVariable(selectedVariable);
      setVariable(selectedVariable);

      const dataKey = selectedVariable.Name.split(' ').join('_');
      const firstTSData = tsData[0] ? tsData[0] : null;

      if (firstTSData && dataKey in firstTSData) {
        setName(`${selectedVariable.Name} [[${selectedVariable.Unit}]]`);
        setTSPlotData(
          tsData.map((ts) => ({
            Time: moment(ts.Time, 'YYYY-MM-DD HH:mm:ss').toDate(),
            Value: ts[dataKey],
          }))
        );
      }
    }
  }, [variables, tsData, defaultValueShortName, updatedTSData]);

  useEffect(() => {
    if (isForAllAnalytics) {
      if (selfTriggered.current) {
        selfTriggered.current = false;
        return;
      }
      if (globalDateRange[0] !== startDate || globalDateRange[1] !== endDate) {
        onUpdateTSPlot(globalDateRange);
        setStartDate(globalDateRange[0]);
        setEndDate(globalDateRange[1]);
      }
    }
  }, [isForAllAnalytics, globalDateRange, startDate, endDate]);

  return {
    tsPlotData,
    previewDatasetId,
    variable,
    tempVariable,
    name,
    startDate,
    endDate,
    previewSettingChanged,
    memoCoords,
    categories,
    selectedDatasetInfo,
    doVariableChange,
    doStartDateChange,
    doEndDateChange,
    onUpdateTSPlot,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate,
  };
};
